import { getTopicsByTheme, themes, topicContentBySlug } from './data';
import type { TopicContent } from './types';

export interface TopicSearchEntry {
    slug: string;
    themeSlug: string;
    themeName: string;
    title: string;
    tags: string[];
    primarySlokaRefs: string[];
    primarySlokaTexts: string[];
}

function normalizeText(text: string | undefined): string {
    if (!text) return '';

    const sanskritMap: Record<string, string> = {
        'ṛ': 'r',
        'Ṛ': 'R',
        'ṇ': 'n',
        'Ṇ': 'N',
        'ṅ': 'n',
        'Ṅ': 'N',
        'ṭ': 't',
        'Ṭ': 'T',
        'ḍ': 'd',
        'Ḍ': 'D',
        'ś': 's',
        'Ś': 'S',
        'ṣ': 's',
        'Ṣ': 'S',
        'ṃ': 'm',
        'Ṃ': 'M',
        'ḥ': 'h',
        'Ḥ': 'H',
        'ā': 'a',
        'Ā': 'A',
        'ī': 'i',
        'Ī': 'I',
        'ū': 'u',
        'Ū': 'U',
        'ñ': 'n',
        'Ñ': 'N',
        'ḷ': 'l',
        'Ḷ': 'L',
        'ḹ': 'l',
        'Ḹ': 'L',
    };

    let normalized = text;
    for (const [diacritic, plain] of Object.entries(sanskritMap)) {
        normalized = normalized.replace(new RegExp(escapeRegExp(diacritic), 'g'), plain);
    }

    return normalized.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const transliterationMap: Record<string, string> = {
    krishna: 'kṛṣṇa',
    'kṛṣṇa': 'krishna',
    vishnu: 'viṣṇu',
    'viṣṇu': 'vishnu',
    shiva: 'śiva',
    'śiva': 'shiva',
    'bhagavad gita': 'bhagavad-gītā',
    'bhagavad-gītā': 'bhagavad gita',
    'bhagavad gītā': 'bhagavad gita',
};

function getTransliterationAlternatives(searchTerm: string): string[] {
    const normalizedSearch = searchTerm.toLowerCase().trim();
    const alternatives = [searchTerm];

    if (transliterationMap[normalizedSearch]) {
        alternatives.push(transliterationMap[normalizedSearch]);
    }

    Object.entries(transliterationMap).forEach(([key, value]) => {
        if (normalizedSearch.includes(key) && !alternatives.includes(value)) {
            alternatives.push(normalizedSearch.replace(key, value));
        }
    });

    return alternatives.filter((item, index) => alternatives.indexOf(item) === index);
}

function normalizeRef(reference: string): string {
    const match = reference.match(/\d+(?:\.\d+)+(?:-\d+)?/);
    return match ? match[0] : reference;
}

function extractPrimaryRefs(content: TopicContent): string[] {
    const primary = content.primarySloka;
    if (!primary) return [];

    const refs = Array.isArray(primary) ? primary.map((sloka) => sloka.reference) : [primary.reference];

    return refs.map(normalizeRef).filter(Boolean);
}

function extractPrimarySlokaTexts(content: TopicContent): string[] {
    const primary = content.primarySloka;
    if (!primary) return [];

    return Array.isArray(primary) ? primary.map((sloka) => sloka.sloka) : [primary.sloka];
}

function buildTopicsSearchIndex(): TopicSearchEntry[] {
    const entries: TopicSearchEntry[] = [];

    themes.forEach((theme) => {
        const topics = getTopicsByTheme(theme.id);

        topics.forEach((topic) => {
            const content = topicContentBySlug[topic.slug];
            const primarySlokaRefs = content ? extractPrimaryRefs(content) : [];
            const primarySlokaTexts = content ? extractPrimarySlokaTexts(content) : [];

            entries.push({
                slug: topic.slug,
                themeSlug: theme.slug,
                themeName: theme.name,
                title: topic.title,
                tags: topic.tags,
                primarySlokaRefs,
                primarySlokaTexts,
            });
        });
    });

    return entries;
}

const TOPICS_SEARCH_INDEX = buildTopicsSearchIndex();

export function getTopicsSearchIndex(): TopicSearchEntry[] {
    return TOPICS_SEARCH_INDEX;
}

const TAG_TOPIC_COUNTS: Readonly<Record<string, number>> = Object.freeze(
    TOPICS_SEARCH_INDEX.reduce<Record<string, number>>((counts, entry) => {
        entry.tags.forEach((tagId) => {
            counts[tagId] = (counts[tagId] ?? 0) + 1;
        });
        return counts;
    }, {}),
);

export function getTagTopicCounts(): Readonly<Record<string, number>> {
    return TAG_TOPIC_COUNTS;
}

export function topicMatchesTag(entry: TopicSearchEntry, tagId: string): boolean {
    return entry.tags.includes(tagId);
}

function normalizeQuery(query: string): string {
    return query.trim().replace(/\s+/g, '.').replace(/\.+/g, '.');
}

export function topicMatchesQuery(entry: TopicSearchEntry, query: string): boolean {
    const queryTrimmed = query.trim();
    if (!queryTrimmed) return false;

    const normalizedAlternatives = getTransliterationAlternatives(queryTrimmed).map((alt) => normalizeText(alt));

    const normalizedTitle = normalizeText(entry.title);
    if (normalizedAlternatives.some((alt) => normalizedTitle.includes(alt))) return true;

    const normalizedPrimarySlokas = entry.primarySlokaTexts.map((sloka) => normalizeText(sloka));
    if (normalizedAlternatives.some((alt) => normalizedPrimarySlokas.some((sloka) => sloka.includes(alt)))) {
        return true;
    }

    const q = normalizeQuery(queryTrimmed);
    const qForRef = q.replace(/\s/g, '');

    return entry.primarySlokaRefs.some(
        (reference) => reference.includes(qForRef) || qForRef.includes(reference),
    );
}
